import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { components, palette, typography } from '../utils/designTokens';

export const tableHeaderCellClassName = `${components.table.header.padding} ${components.table.header.text}`;
export const tableColumnHeaderClassName = `${components.table.header.columnText} ${components.table.header.cellPadding}`;
export const tableCompactHeaderCellClassName = tableColumnHeaderClassName;
export const tableCompactCellClassName = `${typography.size.sm} px-3 py-3`;
export const tableGridHeaderClassName = `grid ${components.table.header.bg} ${components.table.row.border} ${tableColumnHeaderClassName}`;
export const tablePanelTitleClassName = `${typography.size.sm} ${typography.weight.bold} uppercase tracking-[0.12em]`;

export const tableCellTone = {
  body: 'var(--color-table-body)',
  entity: components.table.cell.colors.entity,
  metric: components.table.cell.colors.metric,
  time: components.table.cell.colors.time,
  muted: 'var(--color-table-secondary)',
  supporting: 'var(--color-table-secondary)',
};

export const getTableCellColor = (tone = 'body') => tableCellTone[tone] || tableCellTone.body;

export const TableSortHeader = ({
  active,
  direction,
  label,
  onClick,
  align = 'left',
  activeAccessory = null,
}) => {
  const Icon = active
    ? direction === 'asc'
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;

  return (
    <div className={`inline-flex items-center gap-1 ${align === 'right' ? 'ml-auto' : ''}`}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1"
        style={{ color: palette.text.primary }}
      >
        <span>{label}</span>
        <Icon
          size={active ? 14 : 12}
          strokeWidth={active ? 2.8 : 2}
          style={{
            color: active ? palette.accents.primary.base : getTableCellColor('muted'),
            opacity: active ? 1 : 0.72,
          }}
        />
      </button>
      {active ? activeAccessory : null}
    </div>
  );
};

export const getTableRowBackground = (index, { isHovered = false, isSelected = false } = {}) => {
  if (isSelected) return components.table.row.selection.backgroundColor;
  if (isHovered) return palette.neutrals.panelPrimary;
  return index % 2 === 0 ? palette.neutrals.panelSecondary : palette.neutrals.surfaceSubtle;
};

export const getTableRowStyle = (
  index,
  {
    isHovered = false,
    isSelected = false,
    interactive = false,
    selectedEdge = 'right',
    style = {},
  } = {}
) => {
  const selectedBorder =
    selectedEdge === 'left'
      ? { borderLeft: components.table.row.selection.borderRight }
      : { borderRight: components.table.row.selection.borderRight };

  return {
    backgroundColor: getTableRowBackground(index, { isHovered, isSelected }),
    cursor: interactive ? 'pointer' : undefined,
    ...(isSelected ? selectedBorder : {}),
    ...style,
  };
};

export const getTableHeaderStyle = (style = {}) => ({
  backgroundColor: 'var(--color-table-header-bg)',
  borderBottom: `1px solid ${components.card.borderColor}`,
  ...style,
});

export const TableFrame = ({ children, className = '', style = {}, ...props }) => (
  <div
    className={`${components.card.bg} ${components.card.rounded} overflow-hidden ${className}`}
    style={{ border: `1px solid ${components.card.borderColor}`, ...style }}
    {...props}
  >
    {children}
  </div>
);

export const TableHeaderRow = React.forwardRef(({ as: Element = 'div', children, className = '', style = {}, ...props }, ref) => (
  <Element
    ref={ref}
    className={`${components.table.header.bg} ${components.table.row.border} ${className}`}
    style={style}
    {...props}
  >
    {children}
  </Element>
));

TableHeaderRow.displayName = 'TableHeaderRow';

export const TableHeaderCell = ({
  as: Element = 'div',
  align = 'left',
  children,
  className = '',
  compact = false,
  style = {},
  ...props
}) => (
  <Element
    className={`${compact ? tableCompactHeaderCellClassName : tableHeaderCellClassName} ${className}`}
    style={{ textAlign: align, ...style }}
    {...props}
  >
    {children}
  </Element>
);

export const TableCell = ({
  as: Element = 'div',
  align = 'left',
  children,
  className = '',
  style = {},
  ...props
}) => (
  <Element
    className={`${tableCompactCellClassName} ${className}`}
    style={{ textAlign: align, ...style }}
    {...props}
  >
    {children}
  </Element>
);
